/**
 * preventivePlanDraftStore — Zustand store for live PreventivePlanDraft
 * snapshots (FIX-3, cluster "Salvar Rascunho do plano preventivo").
 *
 * Mirrors `onboardingFlowStore`'s `applySnapshot` pattern: a single source of
 * truth keyed by `draftId`, merged IN PLACE on every engine turn. Before this
 * store existed, every `edit_row`/`add_row` action-only turn (no SSE `chunk`,
 * so `streamingMessageId` stays null) fell into the one-shot branch in
 * `connectionStore.ts`, which minted a brand-new chat message
 * (`id: engine-${Date.now()}`) on EVERY call. `chatStore.addMessage` only
 * dedups by exact id match, so a new id every keystroke meant a new message
 * bubble every keystroke — the builder "replicated" the whole draft table
 * once per edit instead of updating one card.
 *
 * The fix has two parts (both required):
 *  1. `connectionStore.ts` calls `applySnapshot` here whenever a turn carries
 *     `richContent.preventivePlanDraft`, exactly like it already does for
 *     `onboardingFlow`/`equipmentRegistrationFlow`.
 *  2. The one-shot chat message that carries the draft is now keyed by a
 *     STABLE id derived from `draftId` (not `Date.now()`), so
 *     `chatStore.addMessage`'s existing id-dedup REPLACES the message in
 *     place instead of appending a new one (same "sticky id" pattern the
 *     per-turn error bubble already uses — see `errorBubbleId` in
 *     `connectionStore.ts`).
 *
 * `PreventivePlanDraftTable`'s wrapper (`MessageBubble.tsx`) reads the LIVE
 * rows from this store (falling back to the message's own richContent on
 * first render / before any store entry exists), so the table always shows
 * the freshest snapshot regardless of which specific message object it is
 * attached to.
 */
import { create } from 'zustand'
import type { PreventivePlanDraftRichContent } from '../types/preventive-plan'

interface PreventivePlanDraftStore {
  /** Snapshots keyed by draftId. */
  drafts: Record<string, PreventivePlanDraftRichContent>

  /**
   * Merge a snapshot in place — always wins over whatever is stored, since
   * the engine re-emits the FULL row set on every turn (deterministic,
   * flow-context-owned; there is no local mutation to reconcile against).
   */
  applySnapshot: (snapshot: PreventivePlanDraftRichContent) => void

  /** Read a single snapshot by draftId. */
  getDraft: (draftId: string) => PreventivePlanDraftRichContent | undefined

  /** Drop a snapshot once the draft is confirmed/discarded. */
  clear: (draftId: string) => void
}

export const usePreventivePlanDraftStore = create<PreventivePlanDraftStore>((set, get) => ({
  drafts: {},

  applySnapshot: (snapshot) => {
    set((state) => ({ drafts: { ...state.drafts, [snapshot.draftId]: snapshot } }))
  },

  getDraft: (draftId) => get().drafts[draftId],

  clear: (draftId) => {
    set((state) => {
      const next = { ...state.drafts }
      delete next[draftId]
      return { drafts: next }
    })
  },
}))
